import { useQuery } from '@tanstack/react-query';
import type { SalesOrderStatus, PaginationResponse } from 'scheduler-types';
import { apiFetch } from '../lib/api';

// Order list item type (subset of full SalesOrder with summary fields)
export interface OrderListItem {
  id: string;
  gpSalesOrderNumber: string;
  customerId: string;
  customerName: string;
  priority: string;
  status: SalesOrderStatus;
  createdAt: string;
  updatedAt: string;
  expectedShipDate: string | null;
  expectedShipDateWorst: string | null;
  earliestPromisedShipDate: string | null;
  latestPromisedShipDate: string | null;
  lineItemsAtRisk: number;
  lineItemCount: number;
}

interface OrdersResponse {
  data: OrderListItem[];
  pagination: PaginationResponse;
}

interface UseOrdersOptions {
  status?: SalesOrderStatus;
  limit?: number;
  offset?: number;
}

async function fetchOrders(options: UseOrdersOptions = {}): Promise<OrdersResponse> {
  const params = new URLSearchParams();

  if (options.status) {
    params.append('status', options.status);
  }
  if (options.limit !== undefined) {
    params.append('limit', String(options.limit));
  }
  if (options.offset !== undefined) {
    params.append('offset', String(options.offset));
  }

  const queryString = params.toString();
  const path = `/api/v1/orders${queryString ? `?${queryString}` : ''}`;

  return apiFetch<OrdersResponse>(path);
}

export function useOrders(options: UseOrdersOptions = {}) {
  return useQuery({
    queryKey: ['orders', options],
    queryFn: () => fetchOrders(options),
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: false, // Prevent duplicate calls on tab switch
  });
}
